/**
 * alarmMasks — reglas de enmascarado fijo de alarmas (ARS-09), compartidas
 * entre todos los operadores vía /api/alarm-masks (auth-gateway). A
 * diferencia de sessionAlarmMutes, esto persiste en el VPS: un regulador en
 * obras deja de disparar alarma para todo el mundo hasta que un admin lo
 * desmarque, sin pasar por un deploy.
 */
import { create } from 'zustand';

export interface AlarmMask {
  id: string;
  canonicalId: string;
  /** null = enmascara todos los códigos de ese regulador (caso "obras"). */
  code: string | null;
  note: string;
  createdBy: string;
  createdAt: string;
}

interface AlarmMasksState {
  masks: AlarmMask[];
  loading: boolean;
  fetchMasks: () => Promise<void>;
  createMask: (canonicalId: string, code: string | null, note: string) => Promise<boolean>;
  deleteMask: (id: string) => Promise<boolean>;
}

export const useAlarmMasks = create<AlarmMasksState>((set) => ({
  masks: [],
  loading: false,

  fetchMasks: async () => {
    set({ loading: true });
    try {
      const res = await fetch('/api/alarm-masks', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      set({ masks: data.masks ?? [] });
    } catch {
      // sin conexión momentánea: se reintenta en el próximo fetchMasks()
    } finally {
      set({ loading: false });
    }
  },

  createMask: async (canonicalId, code, note) => {
    try {
      const res = await fetch('/api/alarm-masks', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ canonicalId, code, note }),
      });
      if (!res.ok) return false;
      const mask: AlarmMask = await res.json();
      set(s => ({ masks: [...s.masks, mask] }));
      return true;
    } catch {
      return false;
    }
  },

  deleteMask: async (id) => {
    try {
      const res = await fetch(`/api/alarm-masks/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) return false;
      set(s => ({ masks: s.masks.filter(m => m.id !== id) }));
      return true;
    } catch {
      return false;
    }
  },

}));

export function isAlarmMasked(masks: AlarmMask[], canonicalId: string, code: string): boolean {
  return masks.some(m => m.canonicalId === canonicalId && (m.code === null || m.code === code));
}
